import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, PhotoIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { validateCompanyData, INDUSTRIES, COMPANY_SIZES } from '../../../utils/companyValidation';

// Enhanced error display component
const ErrorMessage = ({ error, fieldId }) => {
  if (!error) return null;
  
  return (
    <div 
      className="mt-1 flex items-start space-x-1 text-sm text-red-600"
      role="alert"
      aria-live="polite"
      id={`${fieldId}-error`}
    >
      <ExclamationTriangleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
      <span>{error}</span>
    </div>
  );
};

// Enhanced form field wrapper with consistent error styling
const FormField = ({ 
  label, 
  required = false, 
  error, 
  fieldId, 
  children, 
  className = "" 
}) => {
  return (
    <div className={className}>
      <label 
        htmlFor={fieldId} 
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      <ErrorMessage error={error} fieldId={fieldId} />
    </div>
  );
};

export default function CompanyFormModal({
  isOpen,
  onClose,
  onCreate,
  loading = false,
  context = 'admin' // 'admin' or 'opportunity'
}) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    industry: '',
    location: '',
    website: '',
    logo: '',
    companySize: '',
    foundedYear: ''
  });
  const [errors, setErrors] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  
  // Refs for scroll-to-error functionality
  const modalRef = useRef(null);
  const firstErrorRef = useRef(null);
  const fieldRefs = useRef({});

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setFormData({
        name: '',
        description: '',
        industry: '',
        location: '',
        website: '',
        logo: '',
        companySize: '',
        foundedYear: ''
      });
      setErrors({});
      setLogoFile(null);
      setLogoPreview('');
      setHasAttemptedSubmit(false);
      fieldRefs.current = {};
    }
  }, [isOpen]);

  // Scroll to first error when errors change after submit attempt
  useEffect(() => {
    if (hasAttemptedSubmit && Object.keys(errors).length > 0) {
      scrollToFirstError();
    }
  }, [errors, hasAttemptedSubmit]);

  // Enhanced validation with consistent error handling
  const validateForm = () => {
    const validation = validateCompanyData(formData, logoFile);
    setErrors(validation.errors);
    return validation.isValid;
  };

  // Scroll to first error functionality
  const scrollToFirstError = () => {
    const errorFields = Object.keys(errors);
    if (errorFields.length === 0) return;

    const firstErrorField = errorFields[0];
    const fieldRef = fieldRefs.current[firstErrorField];
    
    if (fieldRef && modalRef.current) {
      // Calculate position relative to modal body
      const modalBody = modalRef.current.querySelector('[data-modal-body]');
      if (modalBody) {
        try {
          const fieldRect = fieldRef.getBoundingClientRect();
          const modalBodyRect = modalBody.getBoundingClientRect();
          const scrollTop = fieldRect.top - modalBodyRect.top + modalBody.scrollTop - 20; // 20px offset
          
          // Check if scrollTo is available (not in test environment)
          if (typeof modalBody.scrollTo === 'function') {
            modalBody.scrollTo({
              top: Math.max(0, scrollTop),
              behavior: 'smooth'
            });
          } else {
            // Fallback for test environment
            modalBody.scrollTop = Math.max(0, scrollTop);
          }
          
          // Focus the field for accessibility
          setTimeout(() => {
            if (fieldRef && typeof fieldRef.focus === 'function') {
              fieldRef.focus();
            }
          }, 300);
        } catch (error) {
          // Silently handle any scrolling errors in test environment
          console.warn('Scroll to error failed:', error);
        }
      }
    }
  };

  // Real-time validation for better UX
  const validateField = (fieldName, value, file = null) => {
    const tempData = { ...formData, [fieldName]: value };
    const tempFile = fieldName === 'logo' ? file : logoFile;
    const validation = validateCompanyData(tempData, tempFile);
    
    return validation.errors[fieldName] || null;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Real-time validation after user has attempted to submit
    if (hasAttemptedSubmit) {
      const fieldError = validateField(name, value);
      setErrors(prev => ({
        ...prev,
        [name]: fieldError
      }));
    } else if (errors[name]) {
      // Clear error when user starts typing (before submit attempt)
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // Real-time validation for logo
      if (hasAttemptedSubmit) {
        const logoError = validateField('logo', null, file);
        setErrors(prev => ({
          ...prev,
          logo: logoError
        }));
      } else if (errors.logo) {
        // Clear logo error
        setErrors(prev => ({
          ...prev,
          logo: ''
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);
    
    if (!validateForm()) {
      // Scroll to first error will be handled by useEffect
      return;
    }

    try {
      const companyData = {
        ...formData,
        foundedYear: formData.foundedYear ? parseInt(formData.foundedYear) : null
      };

      // Handle logo upload if there's a file
      if (logoFile) {
        // In a real implementation, you would upload the file to storage
        // For now, we'll use the preview URL
        companyData.logo = logoPreview;
      }

      await onCreate(companyData);
      
      // Context-specific success messages
      const successMessage = context === 'opportunity' 
        ? 'Company created and selected for opportunity'
        : 'Company created successfully';
      
      toast.success(successMessage);
      handleClose();
    } catch (error) {
      console.error('Error creating company:', error);
      
      // Context-specific error messages
      const errorMessage = context === 'opportunity'
        ? 'Failed to create company for opportunity'
        : 'Failed to create company';
        
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      industry: '',
      location: '',
      website: '',
      logo: '',
      companySize: '',
      foundedYear: ''
    });
    setErrors({});
    setLogoFile(null);
    setLogoPreview('');
    setHasAttemptedSubmit(false);
    fieldRefs.current = {};
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div 
          ref={modalRef}
          className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full max-h-[90vh] flex flex-col"
        >
          {/* Fixed Header */}
          <div className="flex-shrink-0 px-4 pt-5 pb-4 sm:px-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Create New Company
              </h3>
              <button
                type="button"
                className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                onClick={handleClose}
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Scrollable Body */}
          <div 
            className="flex-1 overflow-y-auto px-4 py-6 sm:px-6"
            data-modal-body
          >
            <form id="company-form" onSubmit={handleSubmit} className="space-y-6">
                {/* Logo Upload */}
                <FormField
                  label="Company Logo"
                  error={errors.logo}
                  fieldId="logo"
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      {logoPreview ? (
                        <img
                          className="h-16 w-16 rounded-full object-cover"
                          src={logoPreview}
                          alt="Logo preview"
                        />
                      ) : (
                        <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center">
                          <PhotoIcon className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div>
                      <input
                        type="file"
                        id="logo"
                        accept="image/jpeg,image/png,image/svg+xml"
                        onChange={handleLogoChange}
                        className="hidden"
                        ref={el => fieldRefs.current.logo = el}
                        aria-describedby={errors.logo ? "logo-error" : undefined}
                      />
                      <label
                        htmlFor="logo"
                        className={`cursor-pointer bg-white py-2 px-3 border rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 ${
                          errors.logo ? 'border-red-300' : 'border-gray-300'
                        }`}
                      >
                        Upload Logo
                      </label>
                      <p className="mt-1 text-xs text-gray-500">
                        JPG, PNG, or SVG. Max 5MB.
                      </p>
                    </div>
                  </div>
                </FormField>

                {/* Company Name */}
                <FormField
                  label="Company Name"
                  required
                  error={errors.name}
                  fieldId="name"
                >
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    ref={el => fieldRefs.current.name = el}
                    className={`mt-1 block w-full border rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                      errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter company name"
                    aria-describedby={errors.name ? "name-error" : undefined}
                    aria-invalid={errors.name ? "true" : "false"}
                  />
                </FormField>

                {/* Industry */}
                <FormField
                  label="Industry"
                  required
                  error={errors.industry}
                  fieldId="industry"
                >
                  <select
                    name="industry"
                    id="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    ref={el => fieldRefs.current.industry = el}
                    className={`mt-1 block w-full border rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                      errors.industry ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    aria-describedby={errors.industry ? "industry-error" : undefined}
                    aria-invalid={errors.industry ? "true" : "false"}
                  >
                    <option value="">Select an industry</option>
                    {INDUSTRIES.map(industry => (
                      <option key={industry} value={industry}>
                        {industry}
                      </option>
                    ))}
                  </select>
                </FormField>

                {/* Location */}
                <FormField
                  label="Location"
                  error={errors.location}
                  fieldId="location"
                >
                  <input
                    type="text"
                    name="location"
                    id="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    ref={el => fieldRefs.current.location = el}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    placeholder="e.g., San Francisco, CA"
                    aria-describedby={errors.location ? "location-error" : undefined}
                  />
                </FormField>

                {/* Website */}
                <FormField
                  label="Website"
                  error={errors.website}
                  fieldId="website"
                >
                  <input
                    type="url"
                    name="website"
                    id="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    ref={el => fieldRefs.current.website = el}
                    className={`mt-1 block w-full border rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                      errors.website ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    placeholder="https://example.com"
                    aria-describedby={errors.website ? "website-error" : undefined}
                    aria-invalid={errors.website ? "true" : "false"}
                  />
                </FormField>

                {/* Company Size */}
                <FormField
                  label="Company Size"
                  error={errors.companySize}
                  fieldId="companySize"
                >
                  <select
                    name="companySize"
                    id="companySize"
                    value={formData.companySize}
                    onChange={handleInputChange}
                    ref={el => fieldRefs.current.companySize = el}
                    className={`mt-1 block w-full border rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                      errors.companySize ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    aria-describedby={errors.companySize ? "companySize-error" : undefined}
                  >
                    <option value="">Select company size</option>
                    {COMPANY_SIZES.map(size => (
                      <option key={size.value} value={size.value}>
                        {size.label}
                      </option>
                    ))}
                  </select>
                </FormField>

                {/* Founded Year */}
                <FormField
                  label="Founded Year"
                  error={errors.foundedYear}
                  fieldId="foundedYear"
                >
                  <input
                    type="number"
                    name="foundedYear"
                    id="foundedYear"
                    value={formData.foundedYear}
                    onChange={handleInputChange}
                    ref={el => fieldRefs.current.foundedYear = el}
                    min="1800"
                    max={new Date().getFullYear()}
                    className={`mt-1 block w-full border rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                      errors.foundedYear ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., 2020"
                    aria-describedby={errors.foundedYear ? "foundedYear-error" : undefined}
                    aria-invalid={errors.foundedYear ? "true" : "false"}
                  />
                </FormField>

                {/* Description */}
                <FormField
                  label="Description"
                  error={errors.description}
                  fieldId="description"
                >
                  <textarea
                    name="description"
                    id="description"
                    rows={4}
                    value={formData.description}
                    onChange={handleInputChange}
                    ref={el => fieldRefs.current.description = el}
                    className={`mt-1 block w-full border rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                      errors.description ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Brief description of the company..."
                    aria-describedby={errors.description ? "description-error" : undefined}
                  />
                </FormField>

            </form>
          </div>

          {/* Fixed Footer */}
          <div className="flex-shrink-0 px-4 py-4 sm:px-6 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleClose}
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="company-form"
                disabled={loading}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Company'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}